import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import Card from "../components/Card";
import TrustedBy from "../components/TrustedBy";
import SecuritySection from "../components/SecuritySection";
import SEO from "../components/SEO";
import StructuredData, {
  createServiceSchema,
  createBreadcrumbSchema,
} from "../components/StructuredData";
import { seoData } from "../utils/seo";

// Viewport settings for better animation triggering
const viewportSettings = {
  once: true,
  amount: 0.1,
  margin: "0px 0px -50px 0px",
};

const Planning = () => {
  const benefits = [
    {
      title: "Single interface to track planned activities and assignments",
      image: "/planning/activities-tasks.png",
      points: [
        "Plan your project schedule and tasks side-by-side.",
        "Import schedule baseline from existing tools.",
        "Get a holistic overview of related project items.",
      ],
    },
    {
      title: "Monitor progress of project activities",
      image: "/planning/progress-activities.png",
      points: [
        "Automate progress tracking.",
        "Monitor the completion of all activities.",
        "Verify completed work with photos and documentation.",
      ],
    },
    {
      title: "Easy communication with all relevant parties",
      image: "/planning/communication.png",
      points: [
        "Invite internal and external stakeholders to collaborate.",
        "Get real-time updates on completed activities.",
        "Manage team tasks and assignments.",
      ],
    },
    {
      title: "Have all project documentation in one place",
      image: "/planning/project-files.png",
      points: [
        "Centralised cloud storage for all documentation.",
        "Secure access anytime, from any place.",
        "Integrations with your existing tools (like Google Drive, Dropbox, SharePoint).",
      ],
    },
  ];

  const planningSeo = seoData.planning;
  const structuredData = [
    createServiceSchema(
      "Real Estate Project Planning",
      planningSeo.description,
      planningSeo.url
    ),
    createBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Solutions", url: "/#solutions" },
      { name: "Planning", url: planningSeo.url },
    ]),
  ];

  return (
    <div className="min-h-screen pt-24">
      <SEO
        title={planningSeo.title}
        description={planningSeo.description}
        keywords={planningSeo.keywords}
        url={planningSeo.url}
      />
      <StructuredData data={structuredData} />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="text-center mb-8">
              <h2 className="text-sm uppercase tracking-wider text-primary-600 font-semibold mb-4">
                Modern software FOR PROJECT DEVELOPMENT PLANNING
              </h2>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 sm:mb-6">
                Kickstart your projects with integrated planning to stay on
                track
              </h1>
              <div className="mt-8">
                <Link to="/contact">
                  <Button variant="primary" size="large">
                    Book free demo
                  </Button>
                </Link>
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-12"
            >
              <img
                src="/planning/hero-image.png"
                alt="Planning Dashboard"
                className="w-full"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trusted By Section */}
      <TrustedBy />

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
              Benefits for your business
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={viewportSettings}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Card hover>
                  <img
                    src={benefit.image}
                    alt={benefit.title}
                    className="w-full rounded-lg mb-6 bg-gray-100"
                  />
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {benefit.title}
                  </h3>
                  <ul className="space-y-3">
                    {benefit.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                        <p className="text-gray-600">{point}</p>
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <SecuritySection />

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportSettings}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              Focus on your projects growth.
              <br />
              We'll take care of the reporting.
            </h2>
            <Link to="/contact">
              <Button variant="ghost" size="large">
                Book demo
              </Button>
            </Link>
            <div className="mt-12">
              <p className="text-sm uppercase tracking-wider text-primary-200 mb-2">
                Email
              </p>
              <a
                href="mailto:hello@estabild.com?subject=Interest%20in%20Estabild"
                className="text-2xl font-semibold hover:text-primary-100 transition-colors"
              >
                hello@estabild.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Planning;
